import { PLANNING_DIFFICULTY_GROWTH } from './constants';

const CACHE_KEY = 'btc_market_data';
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

// Hardcoded fallback defaults (updated March 2026)
const FALLBACK_DEFAULTS = {
  btcPrice: 72500,
  difficulty: 144.40,       // in T (trillions)
  blockReward: 3.125,
  difficultyGrowth: PLANNING_DIFFICULTY_GROWTH,  // stated planning assumption, not a forecast
  networkHashrateEH: 1009,
  blockHeight: 939428,
};

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached || !cached.fetchedAt) return null;
    const age = Date.now() - cached.fetchedAt;
    if (age > CACHE_MAX_AGE_MS) return null; // stale
    return cached;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, fetchedAt: Date.now() }));
  } catch { /* localStorage full or unavailable */ }
}

// ── Fetchers (mempool.space primary, blockchain.info/coingecko fallback) ──

async function fetchWithTimeout(url, timeoutMs = 8000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res;
  } finally {
    clearTimeout(timer);
  }
}

async function fetchMempoolData() {
  // Parallel requests to mempool.space — use 3y hashrate endpoint for difficulty history
  const [priceRes, miningRes, diffAdjRes, heightRes] = await Promise.all([
    fetchWithTimeout('https://mempool.space/api/v1/prices'),
    fetchWithTimeout('https://mempool.space/api/v1/mining/hashrate/3y'),
    fetchWithTimeout('https://mempool.space/api/v1/difficulty-adjustment'),
    fetchWithTimeout('https://mempool.space/api/blocks/tip/height'),
  ]);

  const price = await priceRes.json();
  const mining = await miningRes.json();
  const diffAdj = await diffAdjRes.json();
  const blockHeight = parseInt(await heightRes.text(), 10);

  const btcPrice = price.USD;
  const rawDifficulty = mining.currentDifficulty;
  const difficulty = rawDifficulty / 1e12; // convert to T
  const networkHashrateEH = mining.currentHashrate / 1e18; // convert H/s to EH/s

  // Block reward from height (deterministic halving schedule)
  const blockReward = 50 / Math.pow(2, Math.floor(blockHeight / 210000));

  // Observed trailing difficulty growth: log-linear least-squares fit across
  // every retarget point in the window, plus the current difficulty.
  //
  // This is REPORTED but no longer used as the planning default. Deriving the
  // input from a trailing window failed badly in 2026: the 3-year fit returned
  // 37%/yr while difficulty was falling, because the window was dominated by
  // the 2023-2025 build-out. Compounded over a 15-year projection that implied
  // a 114x rise and made every plan lose to buying outright.
  //
  // Planning now uses the stated PLANNING_DIFFICULTY_GROWTH assumption. The
  // observed figures below are surfaced next to the input so the user can see
  // the actual trend and override deliberately.
  const diffPoints = mining.difficulty || [];
  let difficultyGrowth = FALLBACK_DEFAULTS.difficultyGrowth;
  let observedGrowth3y = null;
  let observedGrowth1y = null;
  // Timestamps may be unix seconds or ms — normalize to seconds
  const normalize = (t) => t > 1e12 ? t / 1000 : t;
  const nowSec = Date.now() / 1000;
  const oneYearSec = 365.25 * 24 * 60 * 60;
  const windowSec = 3 * oneYearSec;
  const pts = diffPoints
    .filter(p => p.difficulty > 0 && nowSec - normalize(p.time) <= windowSec)
    .map(p => ({ t: normalize(p.time), ln: Math.log(p.difficulty) }));
  if (mining.currentDifficulty > 0) pts.push({ t: nowSec, ln: Math.log(mining.currentDifficulty) });
  const fitAnnual = (sample) => {
    if (sample.length < 4) return null;
    if (sample[sample.length - 1].t - sample[0].t <= 180 * 24 * 3600) return null;
    const n = sample.length;
    const meanT = sample.reduce((s, p) => s + p.t, 0) / n;
    const meanLn = sample.reduce((s, p) => s + p.ln, 0) / n;
    let num = 0, den = 0;
    for (const p of sample) { num += (p.t - meanT) * (p.ln - meanLn); den += (p.t - meanT) ** 2; }
    if (den <= 0) return null;
    return Math.max(-0.90, Math.min(2.0, Math.exp((num / den) * oneYearSec) - 1));
  };

  observedGrowth3y = fitAnnual(pts);
  observedGrowth1y = fitAnnual(pts.filter(p => nowSec - p.t <= oneYearSec));

  return {
    btcPrice,
    difficulty: Math.round(difficulty * 100) / 100,
    blockReward,
    difficultyGrowth: Math.round(difficultyGrowth * 10000) / 10000,
    // Reported for context, not used for planning — see the note above.
    observedGrowth3y: observedGrowth3y == null ? null : Math.round(observedGrowth3y * 10000) / 10000,
    observedGrowth1y: observedGrowth1y == null ? null : Math.round(observedGrowth1y * 10000) / 10000,
    networkHashrateEH: Math.round(networkHashrateEH),
    difficultyAdjustment: {
      changePct: Math.round(diffAdj.difficultyChange * 100) / 100,
      progressPct: Math.round(diffAdj.progressPercent * 10) / 10,
      remainingBlocks: diffAdj.remainingBlocks,
      estimatedRetargetDate: diffAdj.estimatedRetargetDate * 1000, // to ms
      previousRetarget: diffAdj.previousRetarget,
    },
    blockHeight,
    source: 'mempool.space',
  };
}

async function fetchBlockchainInfoData() {
  const [statsRes, rewardRes] = await Promise.all([
    fetchWithTimeout('https://api.blockchain.info/stats?cors=true'),
    fetchWithTimeout('https://blockchain.info/q/bcperblock?cors=true'),
  ]);

  const stats = await statsRes.json();
  const rewardSats = parseInt(await rewardRes.text(), 10);

  return {
    btcPrice: Math.round(stats.market_price_usd),
    difficulty: Math.round((stats.difficulty / 1e12) * 100) / 100,
    blockReward: rewardSats / 1e8,
    difficultyGrowth: FALLBACK_DEFAULTS.difficultyGrowth, // can't derive from this API
    networkHashrateEH: Math.round(stats.hash_rate * 1e9 / 1e18), // hash_rate is GH/s
    difficultyAdjustment: null,
    blockHeight: null,
    source: 'blockchain.info',
  };
}

async function fetchCoinGeckoPrice() {
  const res = await fetchWithTimeout(
    'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd'
  );
  const data = await res.json();
  return {
    ...FALLBACK_DEFAULTS,
    btcPrice: Math.round(data.bitcoin.usd),
    difficultyAdjustment: null,
    blockHeight: null,
    source: 'coingecko (price only)',
  };
}

// ── Main API ──

export async function fetchMarketData({ force = false } = {}) {
  // Return cache if fresh and not forcing
  if (!force) {
    const cached = readCache();
    if (cached) return { ...cached, fromCache: true };
  }

  // Try APIs in order with fallbacks
  const fetchers = [
    { name: 'mempool.space', fn: fetchMempoolData },
    { name: 'blockchain.info', fn: fetchBlockchainInfoData },
    { name: 'coingecko', fn: fetchCoinGeckoPrice },
  ];

  for (const { name, fn } of fetchers) {
    try {
      const data = await fn();
      writeCache(data);
      return { ...data, fromCache: false };
    } catch (err) {
      console.warn(`[MarketData] ${name} failed:`, err.message);
    }
  }

  // All APIs failed — try stale cache
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const stale = JSON.parse(raw);
      return { ...stale, fromCache: true, stale: true };
    }
  } catch { /* ignore */ }

  // Last resort: hardcoded defaults
  return { ...FALLBACK_DEFAULTS, difficultyAdjustment: null, blockHeight: null, source: 'defaults', fromCache: false, stale: false };
}

export function getCachedMarketData() {
  const cached = readCache();
  if (cached) return cached;

  // Return stale cache if exists
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) return { ...JSON.parse(raw), stale: true };
  } catch { /* ignore */ }

  return null;
}

export { FALLBACK_DEFAULTS };
